import { useGetProvinceAndMunicipalityList } from "@/hooks/useGetProvinceAndMunicipalityList";

type UseGetMunicipalityCodeReturn = {
  getMunicipalityCode: (municipalityName: string) => string;
  getProvinceCode: (provinceName: string) => string;
};

export const useGetMunicipalityCode = (): UseGetMunicipalityCodeReturn => {
  const { municipalities, provinceOptions } = useGetProvinceAndMunicipalityList();

  const normalizeMunicipalityName = (name: string): string => {
    if (!name) return "";

    return name.replace(/.+郡(?=.+(町|村))/, ""); // remove 郡 ONLY if XXX郡YYY町 / 村
  };

  const getActualMunicipalityName = (municipalityName: string): string => {
    const normalizedMunicipalityName = normalizeMunicipalityName(municipalityName);

    const cuttingChars = ["市", "区", "町", "村"];

    // Check for double characters like 市市, 区区, etc.
    for (const char of cuttingChars) {
      const doubleIndex = normalizedMunicipalityName.indexOf(char + char);
      if (doubleIndex !== -1) {
        return normalizedMunicipalityName.slice(0, doubleIndex + 2); // include both characters
      }
    }

    const positions = cuttingChars.map((char) => normalizedMunicipalityName.indexOf(char)).filter((pos) => pos > 0);

    if (positions.length === 0) return normalizedMunicipalityName;

    const cityPos = normalizedMunicipalityName.indexOf("市");
    const districtPos = normalizedMunicipalityName.indexOf("区");

    if (cityPos !== -1 && districtPos !== -1 && cityPos < districtPos) {
      return normalizedMunicipalityName.slice(0, districtPos + 1);
    }

    return normalizedMunicipalityName.slice(0, Math.min(...positions) + 1);
  };

  const getMunicipalityCode = (municipalityName: string) =>
    municipalities
      ?.find((item) => item.municipalityName === municipalityName || item.municipalityName === getActualMunicipalityName(municipalityName))
      ?.municipalityCode?.toString() ?? "";

  const getProvinceCode = (provinceName: string) => provinceOptions?.find((item) => item.label === provinceName)?.value?.toString() ?? "";

  return { getMunicipalityCode, getProvinceCode };
};
